import { useEffect } from 'react';

import Head from 'next/head';
import Slider from 'react-slick';
import { Container } from 'reactstrap';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { fetchProductLists } from '../../apis/products';
import Layout from '../../components/layout/layout';
import ShopItem from '../../components/listing/shop-item';
import useI18n from '../../hooks/use-i18n';
import { languages, contentLanguageMap } from '../../language.config';
import { productListsData, productListsFiltered } from '../../recoil/selectors/productLists';
import getTranslationKeys, { PAGEID } from '../../utils/i18nRosseta';
import END_POINTS, { PAGES_URL } from '../../utils/shopify-endpoints';
import { SHOPIFY_URLS, sortColorOrderReverse } from '../../utils/utils';

import '../../styles/404page.scss';

const NotfoundPage = () => {
  const i18n = useI18n();
  const notFoundPage = i18n.t('notfound_page');
  const shopAllPage = i18n.t('shopall_page');
  const COUNTRY_LANGUAGE = i18n.activeLocale;
  const codes = i18n.activeLocale.split('-');
  const country = codes.length === 2 ? codes[0] : 'sg';
  const pageMetaData = i18n.t('metadata').notfound_page;
  const setProductListData = useSetRecoilState(productListsData);
  const productListFiltered = useRecoilValue(productListsFiltered);
  const sliderSettings = {
    arrows: true,
    accessibility: false,
    infinite: false,
    speed: 500,
    variableWidth: true
  };

  const dataAligner = {
    productKey: 'ZENYUM_ALIGNERS',
    price: shopAllPage.aligners_price,
    productId: 'ZENYUM_ALIGNERS',
    catalogue: {
      page: {
        code: 'ALIGNERS'
      }
    },
    configuration: {
      productImage: {
        imageUrl: '/img/shopall/aligners.jpg'
      },
      discountLabel: '',
      strikeoutPrice: 0
    }
  };

  useEffect(() => {
    getProductLists();
  }, [country]);

  const getProductLists = async () => {
    const recommendations = await fetchProductLists([]);
    if (recommendations) setProductListData({ allProducts: sortColorOrderReverse(recommendations) });
  };

  return (
    <Layout
      page="404"
      title={pageMetaData.title}
      insiderPage="404"
      description={pageMetaData.description}
      keywords={pageMetaData.keywords}
      analyticData={{ name: '404', properties: { } }}
      pageUrl="404"
      pageEnabledMarkets={[]}
    >
      <Head>
        <link rel="stylesheet" href="/vendor/slick/slick.min.css" />
        <link rel="stylesheet" href="/vendor/slick/slick-theme.min.css" defer={true} />
      </Head>
      <section>
        <div className="notfound-wrapper">
          <img src="/img/img-notfound.png" alt="Zenyum - Page not found" />
          <h1>{notFoundPage.title}</h1>
          <p>{notFoundPage.description}</p>
          <a
            className="web-btn web-btn--fill"
            referrerPolicy="no-referrer"
            rel="noreferrer"
            href={`${SHOPIFY_URLS[COUNTRY_LANGUAGE]}${PAGES_URL}${END_POINTS.SHOPALL}`}
            target="_blank"
          >
            {notFoundPage.button}
          </a>
        </div>
      </section>
      <section className="nf-bottom-products">
        <Container>
          <h2>{notFoundPage.section_title}</h2>
          <div className="nf-list-slider">
            <Slider {...sliderSettings}>
              <div className="img-slide-wrapper">
                <ShopItem
                  key={dataAligner.productId}
                  type="aligner"
                  dataLoaded={productListFiltered?.allProducts}
                  data={dataAligner}
                />
              </div>
              {productListFiltered.allProducts && productListFiltered.allProducts.map((product) => (
                <div className="img-slide-wrapper">
                  <ShopItem key={product.productId} data={product} />
                </div>
              ))}
            </Slider>
          </div>
        </Container>
      </section>
    </Layout>
  );
};

export async function getStaticProps({ params }) {
  const lngDict = {};
  const pageId = PAGEID.ERROR_PAGE;
  const keys = getTranslationKeys(pageId);
  const { default: lngDictAll = {} } = await import(
    `../../locales/${contentLanguageMap[params.lng]}.json`
  );
  keys.forEach((data) => {
    lngDict[data] = lngDictAll[data];
  });
  return {
    props: { lng: params.lng, lngDict, pageId }
  };
}

export async function getStaticPaths() {
  return {
    paths: languages.map((l) => ({ params: { lng: l } })),
    fallback: false
  };
}

export default NotfoundPage;
